import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AppLanguageService, TranslationService } from '@ace/ace';
import { Subscription } from 'rxjs';
import { LoaderService } from './services/loader.service';
import { patchAceSmtpPageTitle } from './core/patches/ace-smtp-title.patch';


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit, OnDestroy {
  menu;
  title = 'ABG';
  private languageSubscription?: Subscription;

  constructor(
    private router: Router,
    private loader: LoaderService,
    private appLanguage: AppLanguageService,
    private translate: TranslateService,
    private aceTranslation: TranslationService,
  ) {
    // The library's SMTP list renders its heading without translating it.
    patchAceSmtpPageTitle(aceTranslation);

    loader.hideLoader();
    router.events.subscribe((event: any) => {
      if (event instanceof RouteConfigLoadStart) {
        loader.showLoader();
      } else if (event instanceof RouteConfigLoadEnd) {
        loader.hideLoader();
      }
    })
  }

  ngOnInit(): void {
    this.loadTheme();
    this.syncTranslationLanguage();
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  /**
   * `AppLanguageService` drives the library's own dictionary but never touches
   * ngx-translate, so without this bridge every `| translate` in the app stays
   * pinned to the default language no matter what the header switcher does.
   */
  private syncTranslationLanguage(): void {
    // The switcher persists its choice before Angular boots, so honour it up
    // front rather than flashing English until the language list resolves.
    const storedLang = localStorage.getItem('appLang');
    if (storedLang) {
      this.translate.use(storedLang.toLowerCase());
    }

    this.languageSubscription = this.appLanguage.selectedLanguage$.subscribe((language) => {
      const isoCode = language?.isoCode?.toLowerCase();
      if (isoCode && isoCode !== this.translate.currentLang) {
        this.translate.use(isoCode);
      }
    });
  }

  loadTheme() {
    let theme = localStorage.getItem("selectedTheme")
    if (!theme) {
      theme = "theme1"
      localStorage.setItem("selectedTheme", "theme1");
    }
    if (theme == "theme1") {
      const sourceColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light');
      const sourceColor_1 = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-1');
      const sourceColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-2');
      const shadowColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-shadow-color');
      const chartLabelColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-chart-label-color');
      document.documentElement.style.setProperty('--default-color', sourceColor);
      document.documentElement.style.setProperty('--default-color-1', sourceColor_1);
      document.documentElement.style.setProperty('--default-color-2', sourceColor_2);
      document.documentElement.style.setProperty('--default-shadow-color', shadowColor);
      document.documentElement.style.setProperty('--default-chart-label-color', chartLabelColor);

      const backgroundColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-light-bg-color');
      document.documentElement.style.setProperty('--default-bg-color', backgroundColor);

      document.documentElement.style.setProperty('--white-color', '#fff');

    } else if (theme == "theme2") {
      const sourceColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark');
      const sourceColor_1 = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-1');
      const sourceColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-2');
      const shadowColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-shadow-color');
      const chartLabelColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-chart-label-color');
      document.documentElement.style.setProperty('--default-color', sourceColor);
      document.documentElement.style.setProperty('--default-color-1', sourceColor_1);
      document.documentElement.style.setProperty('--default-color-2', sourceColor_2);
      document.documentElement.style.setProperty('--default-shadow-color', shadowColor);
      document.documentElement.style.setProperty('--default-chart-label-color', chartLabelColor);

      const backgroundColor = getComputedStyle(document.documentElement).getPropertyValue('--primary-dark-bg-color');
      document.documentElement.style.setProperty('--default-bg-color', backgroundColor);


      const defaultColor_2 = getComputedStyle(document.documentElement).getPropertyValue('--default-color-2');
      document.documentElement.style.setProperty('--option-default-2', defaultColor_2);

      document.documentElement.style.setProperty('--menu-bg-color', sourceColor);
      document.documentElement.style.setProperty('--Text-color', '#fff');
      document.documentElement.style.setProperty('--white-color', sourceColor);
      document.documentElement.style.setProperty('--grid-data-color', '#fff');
      // Make form labels and titles white in dark mode
      document.documentElement.style.setProperty('--label-color', '#fff');

    }
  }
}
